import React, { useState } from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { todayLocal } from "../../lib/utils";

function toLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shiftDate(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalISODate(d);
}

function startOfWeek(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return shiftDate(dateStr, -d.getDay());
}

function monthBounds(dateStr, monthOffset) {
  const d = new Date(`${dateStr}T00:00:00`);
  const from = new Date(d.getFullYear(), d.getMonth() + monthOffset, 1);
  const to = new Date(d.getFullYear(), d.getMonth() + monthOffset + 1, 0);
  return [toLocalISODate(from), toLocalISODate(to)];
}

function fmt(dateStr) {
  if (!dateStr) return "";
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function buildPresets(t) {
  const [lastMonthFrom, lastMonthTo] = monthBounds(t, -1);
  return [
    { label: "Today", from: t, to: t },
    { label: "Yesterday", from: shiftDate(t, -1), to: shiftDate(t, -1) },
    { label: "This Week", from: startOfWeek(t), to: t },
    { label: "Last 7 Days", from: shiftDate(t, -6), to: t },
    { label: "Last 30 Days", from: shiftDate(t, -29), to: t },
    { label: "Last Month", from: lastMonthFrom, to: lastMonthTo },
  ];
}

export function DateRangeFilter({ from, to, onChange, testId = "route-date-filter" }) {
  const [open, setOpen] = useState(false);
  const t = todayLocal();
  const presets = buildPresets(t);
  const isSingle = from && to && from === to;

  const label = !from && !to ? "All Dates" : isSingle ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;

  const apply = (f, tt) => { onChange({ from: f, to: tt }); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-auto justify-start gap-2" data-testid={`${testId}-trigger`}>
          <CalendarIcon size={14} strokeWidth={1.5} /> {label} <ChevronDown size={14} strokeWidth={1.5} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start" data-testid={`${testId}-popover`}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Quick Ranges</p>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button
              key={p.label} type="button" onClick={() => apply(p.from, p.to)}
              className="rounded-full border border-border px-2.5 py-1 text-xs text-text-main transition-colors hover:border-primary hover:text-primary"
              data-testid={`${testId}-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Custom (pick one day or a range)</p>
        <div className="flex items-center gap-2">
          <Input
            type="date" value={from || ""} className="text-xs"
            onChange={(e) => onChange({ from: e.target.value, to: to && to >= e.target.value ? to : e.target.value })}
            data-testid={`${testId}-from-input`}
          />
          <span className="text-text-muted">–</span>
          <Input
            type="date" value={to || ""} className="text-xs"
            onChange={(e) => onChange({ from: from && from <= e.target.value ? from : e.target.value, to: e.target.value })}
            data-testid={`${testId}-to-input`}
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <button type="button" className="text-xs text-text-muted hover:text-primary" onClick={() => apply(t, t)} data-testid={`${testId}-single-today-shortcut`}>
            Just today
          </button>
          <Button variant="ghost" size="sm" onClick={() => { onChange({ from: "", to: "" }); setOpen(false); }} data-testid={`${testId}-clear-button`}>
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
